// src/web/client/src/platform/api.js
//
// The Platform screens' data client. Replaces ecomoria-platform's web/src/api.js
// (which spoke to the Platform's own /api with an OPS token or a Platform session
// token) with THIS app's authenticatedFetch to THIS app's /api/platform proxy —
// which forwards server-to-server for the one shop the verified session token
// names (backend routes/platformRoutes.js). The shop is never sent from here:
// the proxy takes it from the session, so there is no ?shop= to get wrong.
//
// Same surface as the Platform client for the four screens it serves, same error
// contract: a non-2xx throws an Error carrying `.status` and the server's
// machine-readable `.code`, so screens render a precise state.

import { authenticatedFetch } from '@/utils/authenticatedFetch';

// The app-wide base URL convention (VITE_API_BASE_URL, defaulting to
// https://api.ecomoria.com/api), normalised so it ALWAYS ends in /api: the
// backend mounts the proxy at /api/platform, and a base configured without the
// /api suffix must not silently turn every Platform call into a 404.
function apiBase() {
  const raw = String(import.meta.env.VITE_API_BASE_URL || 'https://api.ecomoria.com/api').replace(/\/+$/, '');
  return /\/api$/.test(raw) ? raw : `${raw}/api`;
}

export const platformUrl = (path) => `${apiBase()}/platform${path}`;

const withParams = (base, params) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) if (v != null && v !== '') sp.set(k, v);
  const q = sp.toString();
  return q ? `${base}?${q}` : base;
};

async function get(path, params) {
  const res = await authenticatedFetch(platformUrl(withParams(path, params)));
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = detail.code || null;
    throw err;
  }
  return res.json();
}

// Settings writes — the ONLY writes the proxy forwards (routes/platformRoutes.js PUT list).
async function put(path, body) {
  const res = await authenticatedFetch(platformUrl(path), { method: 'PUT', body: JSON.stringify(body || {}) });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const err = new Error(detail.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = detail.code || null;
    throw err;
  }
  return res.json();
}

export const getBrainStats = () => get('/brain/stats');
export const getBrainActivity = () => get('/brain/activity');
export const getLifecycle = () => get('/lifecycle');
export const getBoard = (params) => get('/board', params);
export const getRiskBoard = (params) => get('/risk-board', params);
// Replenish
export const getSummary = () => get('/dashboard/summary');
export const getUpcomingReorders = () => get('/reorders/upcoming');
export const getProducts = () => get('/products');
export const saveProductConsumption = (sku, body) => put(`/products/${encodeURIComponent(sku)}/consumption`, body);
export const getSettings = () => get('/settings');
export const saveSettings = (body) => put('/settings', body);
export const getReminderTemplate = () => get('/reminders/template');
export const saveReminderTemplate = (body) => put('/reminders/template', body);
// Customer 360
export const getCustomerProfile = (id) => get(`/customers/${encodeURIComponent(id)}/profile`);
export const getCustomerNextAction = (id) => get(`/customers/${encodeURIComponent(id)}/next-action`);
export const getCustomerProgress = (id) => get(`/customers/${encodeURIComponent(id)}/progress`);
// Reviews / Loyalty / Revenue / Timeline
export const getReviewsSummary = () => get('/reviews/summary');
export const getReviewsRequests = () => get('/reviews/requests');
export const getLoyaltySummary = () => get('/loyalty/summary');
export const getLoyaltyMembers = () => get('/loyalty/members');
export const getRevenueRecovered = () => get('/revenue-recovered');
export const getActivityTimeline = (params) => get('/activity-timeline', params);

// Export helpers: pull EVERY page of a paged list under the current filters
// (server pageSize max 100), so an export is the whole filtered set — not just
// the page on screen. Capped for safety. Verbatim from the Platform client.
async function allPages(fetchPage, params, { cap = 5000 } = {}) {
  const rows = [];
  let page = 1;
  let res;
  do {
    res = await fetchPage({ ...params, page, pageSize: 100 });
    rows.push(...(res.rows || []));
    page += 1;
  } while ((res.rows || []).length === 100 && rows.length < (res.total ?? Infinity) && rows.length < cap);
  return { ...res, rows, truncated: rows.length >= cap && rows.length < (res.total ?? 0) };
}
export const getRiskBoardAll = (params) => allPages(getRiskBoard, params);
export const getBoardAll = (params) => allPages(getBoard, params);
